import { useEffect, useState } from "react";
import { Button } from "@/shared/ui/button";
import { AlertTriangle, Copy, Loader2, Play, Sparkles } from "lucide-react";
import { toast } from "sonner";
import {
  buildCurl,
  buildRequestUrl,
  executeEndpoint,
  type Endpoint,
  type ParamValues,
} from "../developer-api";
import { MethodBadge } from "./atoms";
import { copyToClipboard, initialValuesFor, missingRequired, type RequestResult } from "./helpers";
import { ParamRow, ParamSection } from "./paramFields";
import { ResponsePanel } from "./ResponsePanel";

export function EmptyPlayground() {
  return (
    <div className="flex flex-col items-center justify-center text-center py-24 px-6 border border-dashed border-border rounded-lg">
      <Sparkles className="h-10 w-10 text-muted-foreground/60 mb-3" />
      <h3 className="text-base font-semibold">Pick an endpoint to start</h3>
      <p className="text-sm text-muted-foreground max-w-sm mt-1">
        Choose any endpoint from the left to fill in parameters and send a live
        request against the API.
      </p>
    </div>
  );
}

export function PlaygroundPanel({
  endpoint,
  token,
  clientId,
  result,
  onResult,
  values: valuesProp,
  onValues,
}: {
  endpoint: Endpoint;
  token: string | null;
  clientId?: string;
  result: RequestResult | null;
  onResult: (id: string, result: RequestResult | null) => void;
  values: ParamValues | null;
  onValues: (id: string, values: ParamValues) => void;
}) {
  const [values, setValuesLocal] = useState<ParamValues>(() =>
    valuesProp ?? initialValuesFor(endpoint, clientId),
  );
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setResult = (r: RequestResult | null) => onResult(endpoint.id, r);
  const setValues = (v: ParamValues) => {
    setValuesLocal(v);
    onValues(endpoint.id, v);
  };

  useEffect(() => {
    setValuesLocal(valuesProp ?? initialValuesFor(endpoint, clientId));
    setError(null);
  }, [endpoint, clientId]);

  const missing = missingRequired(endpoint, values);
  const url = buildRequestUrl(endpoint, values);
  const curl = buildCurl(endpoint, values, token);

  const onSend = async () => {
    if (!token) {
      toast.error("No auth token — sign in first.");
      return;
    }
    if (missing.length > 0) {
      toast.error(`Missing required: ${missing.join(", ")}`);
      return;
    }
    setRunning(true);
    setError(null);
    const started = performance.now();
    try {
      const r = await executeEndpoint(endpoint, values, token);
      const latencyMs = Math.round(performance.now() - started);
      const ok = r.status >= 200 && r.status < 300 && !r.error;
      const payload = r.error ? r.error : r.data;
      setResult({
        status: r.status,
        statusText: r.statusText,
        latencyMs,
        count: r.count,
        body: JSON.stringify(payload, null, 2),
        ok,
      });
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
      setResult(null);
    } finally {
      setRunning(false);
    }
  };

  const pathParams = endpoint.params.filter((p) => p.in === "path");
  const queryParams = endpoint.params.filter((p) => p.in === "query");

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <div className="flex items-center gap-2 flex-wrap">
          <MethodBadge method={endpoint.method} />
          <code className="text-sm font-mono text-foreground break-all">
            {endpoint.path}
          </code>
        </div>
        <h2 className="text-xl font-semibold tracking-tight">
          {endpoint.summary}
        </h2>
        <p className="text-sm text-muted-foreground leading-relaxed max-w-3xl">
          {endpoint.description}
        </p>
      </div>

      <div className="rounded-lg border border-border bg-muted/20 px-3 py-2">
        <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">
          Request URL
        </div>
        <code className="block text-xs font-mono break-all">{url}</code>
      </div>

      {(pathParams.length > 0 || queryParams.length > 0) && (
        <div className="rounded-lg border border-border">
          {pathParams.length > 0 && (
            <ParamSection title="Path parameters">
              {pathParams.map((p) => (
                <ParamRow
                  key={p.name}
                  param={p}
                  value={values[p.name] ?? ""}
                  onChange={(v) => setValues({ ...values, [p.name]: v })}
                  error={missing.includes(p.name)}
                />
              ))}
            </ParamSection>
          )}
          {queryParams.length > 0 && (
            <ParamSection
              title="Query parameters"
              border={pathParams.length > 0}
            >
              {queryParams.map((p) => (
                <ParamRow
                  key={p.name}
                  param={p}
                  value={values[p.name] ?? ""}
                  onChange={(v) => setValues({ ...values, [p.name]: v })}
                  error={false}
                />
              ))}
            </ParamSection>
          )}
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <Button
          onClick={() => void onSend()}
          disabled={running || !token}
          className="gap-2"
        >
          {running ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Play className="h-4 w-4" />
          )}
          Send Request
        </Button>
        <Button
          variant="outline"
          onClick={() => copyToClipboard(curl, "cURL")}
        >
          <Copy className="h-4 w-4 mr-2" />
          Copy as cURL
        </Button>
        {missing.length > 0 && (
          <span className="text-xs text-red-600 dark:text-red-400 inline-flex items-center gap-1 font-medium">
            <AlertTriangle className="h-3.5 w-3.5" />
            Required: {missing.join(", ")}
          </span>
        )}
      </div>

      {error && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm">
          <div className="flex items-center gap-2 font-medium text-red-700 dark:text-red-300">
            <AlertTriangle className="h-4 w-4" />
            Request failed
          </div>
          <div className="mt-1 text-xs text-red-700/80 dark:text-red-300/80 font-mono">
            {error}
          </div>
        </div>
      )}

      {result && <ResponsePanel result={result} curl={curl} />}
    </div>
  );
}
